import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, CircularProgress, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  FormControl, InputLabel, Select, MenuItem, IconButton, Tooltip,
  Link, ButtonGroup
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import TranslateIcon from '@mui/icons-material/Translate';
import { booksAPI } from '../../services/api';

const BookTranslationsPanel = ({ bookId, setSnackbar }) => {
  const navigate = useNavigate();
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState({
    translationBookId: '',
    language: ''
  });
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [loadingButtons, setLoadingButtons] = useState({});

  // Fetch translations
  const fetchTranslations = async () => {
    try {
      setLoading(true);
      const response = await booksAPI.getBookTranslations(bookId);
      setTranslations(response.data.translations || []);
    } catch (error) {
      console.error('Error fetching translations:', error);
      setSnackbar({
        open: true,
        message: 'Failed to fetch translations',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTranslations();
  }, [bookId]);

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setFormData({
      translationBookId: '',
      language: ''
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await booksAPI.addTranslation(bookId, formData);
      setSnackbar({
        open: true,
        message: 'Translation added successfully',
        severity: 'success'
      });
      handleCloseDialog();
      fetchTranslations();
    } catch (error) {
      console.error('Error adding translation:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to add translation',
        severity: 'error'
      });
    }
  };

  const handleDeleteTranslation = async (translationId) => {
    if (!window.confirm('Are you sure you want to remove this translation?')) {
      return;
    }

    try {
      await booksAPI.deleteTranslation(bookId, translationId);
      setSnackbar({
        open: true,
        message: 'Translation removed successfully',
        severity: 'success'
      });
      fetchTranslations();
    } catch (error) {
      console.error('Error removing translation:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove translation',
        severity: 'error'
      });
    }
  };

  const handleTranslationClick = (translationBookId) => {
    navigate(`/books/${translationBookId}`);
  };

  const handleTranslateContent = async (translation, textType) => {
    if (translation.language === 'en') {
      setSnackbar({
        open: true,
        message: 'Translation is only available for non-English books',
        severity: 'warning'
      });
      return;
    }

    const buttonKey = `${translation.id}-${textType}`;
    try {
      setLoadingButtons(prev => ({ ...prev, [buttonKey]: true }));
      await booksAPI.translateBookContent(
        translation.translationBookId,
        bookId,
        translation.language.toLowerCase(),
        textType
      );
      setSnackbar({
        open: true,
        message: 'Content translation started successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error translating content:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || 'Failed to translate content',
        severity: 'error'
      });
    } finally {
      setLoadingButtons(prev => ({ ...prev, [buttonKey]: false }));
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h2">
          Translations
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenDialog}
        >
          Add Translation
        </Button>
      </Box>

      {translations.length === 0 ? (
        <Alert severity="info">No translations available for this book.</Alert>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Language</TableCell>
                <TableCell>Title</TableCell>
                <TableCell>Author</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {translations.map((translation) => (
                <TableRow 
                  key={translation.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleTranslationClick(translation.translationBookId)}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <LanguageIcon sx={{ mr: 1 }} />
                      {translation.language.toUpperCase()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {translation.translationBook.title}
                      <OpenInNewIcon sx={{ ml: 1, fontSize: '0.875rem', color: 'text.secondary' }} />
                    </Box>
                  </TableCell>
                  <TableCell>{translation.translationBook.author}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                      {translation.language !== 'en' && (
                        <ButtonGroup size="small" onClick={(e) => e.stopPropagation()}>
                          <Tooltip title="Translate short version">
                            <Button
                              color="primary"
                              onClick={() => handleTranslateContent(translation, 'short')}
                              disabled={loadingButtons[`${translation.id}-short`]}
                              size="small"
                            >
                              {loadingButtons[`${translation.id}-short`] ? (
                                <CircularProgress size={20} />
                              ) : (
                                'Short'
                              )}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Translate medium version">
                            <Button
                              color="primary"
                              onClick={() => handleTranslateContent(translation, 'medium')}
                              disabled={loadingButtons[`${translation.id}-medium`]}
                              size="small"
                            >
                              {loadingButtons[`${translation.id}-medium`] ? (
                                <CircularProgress size={20} />
                              ) : (
                                'Medium'
                              )}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Translate long version">
                            <Button
                              color="primary"
                              onClick={() => handleTranslateContent(translation, 'long')}
                              disabled={loadingButtons[`${translation.id}-long`]}
                              size="small"
                            >
                              {loadingButtons[`${translation.id}-long`] ? (
                                <CircularProgress size={20} />
                              ) : (
                                'Long'
                              )}
                            </Button>
                          </Tooltip>
                        </ButtonGroup>
                      )}
                      <Tooltip title="Remove translation">
                        <IconButton
                          color="error"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTranslation(translation.id);
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Translation</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Translation Book ID"
                name="translationBookId"
                value={formData.translationBookId}
                onChange={handleInputChange}
                required
                fullWidth
                helperText="Enter the ID of the book to add as a translation"
              />

              <FormControl fullWidth>
                <InputLabel id="language-select-label">Language</InputLabel>
                <Select
                  labelId="language-select-label"
                  name="language"
                  value={formData.language}
                  onChange={handleInputChange}
                  label="Language"
                  required
                >
                  <MenuItem value="en">English</MenuItem>
                  <MenuItem value="es">Spanish</MenuItem>
                  <MenuItem value="fr">French</MenuItem>
                  <MenuItem value="de">German</MenuItem>
                  <MenuItem value="it">Italian</MenuItem>
                  <MenuItem value="pt">Portuguese</MenuItem>
                  <MenuItem value="ru">Russian</MenuItem>
                  <MenuItem value="zh">Chinese</MenuItem>
                  <MenuItem value="ja">Japanese</MenuItem>
                  <MenuItem value="ko">Korean</MenuItem>
                  <MenuItem value="ar">Arabic</MenuItem>
                  <MenuItem value="hi">Hindi</MenuItem>
                  <MenuItem value="tr">Turkish</MenuItem>
                  <MenuItem value="pl">Polish</MenuItem>
                  <MenuItem value="nl">Dutch</MenuItem>
                  <MenuItem value="sv">Swedish</MenuItem>
                  <MenuItem value="da">Danish</MenuItem>
                  <MenuItem value="no">Norwegian</MenuItem>
                  <MenuItem value="fi">Finnish</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={loadingBooks}>
              {loadingBooks ? <CircularProgress size={24} /> : 'Add Translation'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Paper>
  );
};

export default BookTranslationsPanel; 